import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RateBasedHotelRoomCardRatesProps } from "@b2bportal/core-hotels";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getHasDiscount } from "@tenant/hotels";
import { useBaseRateSelection } from "./useBaseRateSelection";
import { useHotelRoomNightlyPricing } from "./useHotelRoomNightlyPricing";
import { useMealPlanRatesForBaseRate } from "./useMealPlanRatesForBaseRate";
import { HotelsShopSelectors } from "~core-hotels/store";

export const useHotelRoomCardRates = ({
  rates,
  selectedRate,
  nightCount,
  setSelectedRate,
}: RateBasedHotelRoomCardRatesProps) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();

  const selectedProduct = selectedRate.product;

  const { nightlyPrice, nightlyPoints, showPointsPricing, rewardsAccountId } =
    useHotelRoomNightlyPricing({ selectedProduct });

  const roomCount = useSelector(HotelsShopSelectors.getRoomsCount);

  const totalDA = selectedProduct.totalDiscountAwareTaxesAndFeesIncluded;
  const totalDiscounted =
    totalDA.priceWithAllDiscounts ?? totalDA.priceWithUnmanagedDiscounts;
  const totalPriceWithNoDiscounts = totalDA.priceWithNoDiscounts;

  const totalHasDiscount = getHasDiscount(totalDA);

  const totalNotDiscountedPrice = totalHasDiscount
    ? formatFiatCurrency(totalPriceWithNoDiscounts.fiat)
    : null;
  const totalNotDiscountedPoints = totalHasDiscount
    ? (formatRewardsCurrency(
        totalPriceWithNoDiscounts.accountSpecific?.[rewardsAccountId],
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      ) ?? null)
    : null;

  const totalPrice = formatFiatCurrency(
    totalHasDiscount ? totalDiscounted.fiat : totalPriceWithNoDiscounts.fiat
  );
  const totalPoints =
    formatRewardsCurrency(
      totalHasDiscount
        ? totalDiscounted.accountSpecific?.[rewardsAccountId]
        : totalPriceWithNoDiscounts.accountSpecific?.[rewardsAccountId],
      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    ) ?? "";

  const { baseRates, selectedBaseRate, onSelectBaseRateId } =
    useBaseRateSelection({ rates, selectedRate, setSelectedRate });

  const { mealPlanRatesForBaseRate, showMealPlans } =
    useMealPlanRatesForBaseRate({
      rates,
      baseRate: selectedBaseRate,
    });

  const text = useMemo(
    () => ({
      selectedPrice: t("core-hotels.shop.price.pricePerNight", {
        price: nightlyPrice,
      }),
      totalPrice:
        (roomCount ?? 0) > 1
          ? t("core-hotels.shop.price.priceForNightsAndRoomsWithTaxesAndFees", {
              price: totalPrice,
              count: nightCount,
              roomCount: roomCount,
            })
          : t("core-hotels.shop.price.priceForNightsWithTaxesAndFees", {
              price: totalPrice,
              count: nightCount,
            }),
    }),
    [t, nightlyPrice, totalPrice, nightCount, roomCount]
  );

  return useMemo(
    () => ({
      context: {
        rates,
        selectedRate,
        selectedBaseRate,
        baseRates,
      },
      onSelectBaseRateId,
      text,
      totalPrice,
      totalNotDiscountedPrice: showPointsPricing
        ? (totalNotDiscountedPoints ?? totalNotDiscountedPrice)
        : totalNotDiscountedPrice,
      nightlyPrice,
      roomCount,
      nightCount,
      nightlyPoints,
      totalPoints,
      showPointsPricing,
      mealPlanRatesForBaseRate,
      showMealPlans,
    }),
    [
      rates,
      selectedRate,
      selectedBaseRate,
      baseRates,
      onSelectBaseRateId,
      text,
      nightCount,
      nightlyPrice,
      roomCount,
      totalPrice,
      totalNotDiscountedPrice,
      totalNotDiscountedPoints,
      nightlyPoints,
      totalPoints,
      showPointsPricing,
      mealPlanRatesForBaseRate,
      showMealPlans,
    ]
  );
};
